/* eslint-disable no-console */
import { AxiosRequestConfig, AxiosResponse } from 'axios';

import { tourResponse } from './tour-response';

/**
 * The sample-data router.
 *
 * Every read a tour triggers arrives here instead of the network. A resolver is
 * matched on method + path and returns the payload the live endpoint would have
 * returned, built from the frozen fixtures plus this run's demo state.
 *
 * This is deliberately *not* a mock HTTP server: there is no server, no
 * handler transport and no serialisation — just a lookup table from the request
 * the service layer already built to a local function. It sits at the one place
 * every call passes through so the ~40 pages a tour visits need no changes.
 */

export interface TourRequestContext {
  method: string;
  /** Path with the base URL and query string stripped, e.g. `/AwardManagement/user`. */
  path: string;
  /** Values captured by the pattern's `:name` segments. */
  params: Record<string, string>;
  query: URLSearchParams;
  /** Parsed request body for writes. */
  body: any;
  config: AxiosRequestConfig;
}

/** Returns the payload that goes inside the response envelope's `data`. */
export type TourResolver = (ctx: TourRequestContext) => unknown;

export interface TourRoute {
  /** HTTP method, or '*' to match any. */
  method: string;
  /**
   * Path pattern. `:name` captures one segment, a trailing `*` matches the
   * rest of the path. Matching ignores case and a trailing slash.
   */
  pattern: string;
  resolve: TourResolver;
}

const routes: TourRoute[] = [];

/** Register sample-data routes. Later registrations win over earlier ones. */
export const registerTourRoutes = (entries: TourRoute[]) => {
  // Unshift so a domain registered later can override a broader earlier match.
  routes.unshift(...entries);
};

/** Drop every registered route. Test-only. */
export const clearTourRoutes = () => {
  routes.length = 0;
};

const splitPath = (path: string): string[] => path.split('/').filter(Boolean);

interface PatternMatch {
  params: Record<string, string>;
  /** Lower is more specific: literal segments beat captures beat wildcards. */
  score: number;
}

const matchPattern = (pattern: string, path: string): PatternMatch | null => {
  const patternParts = splitPath(pattern);
  const pathParts = splitPath(path);
  const params: Record<string, string> = {};
  let score = 0;

  for (const [index, part] of patternParts.entries()) {
    if (part === '*') {
      // A trailing wildcard swallows the rest, and is the weakest match.
      return { params, score: score + 100 };
    }
    const actual = pathParts[index];
    if (actual === undefined) return null;
    if (part.startsWith(':')) {
      params[part.slice(1)] = decodeURIComponent(actual);
      score += 1;
      continue;
    }
    if (part.toLowerCase() !== actual.toLowerCase()) return null;
  }

  return patternParts.length === pathParts.length ? { params, score } : null;
};

/**
 * Split a request config into the path, query and body a resolver works with.
 * The services build URLs in every style axios allows — a full query string
 * baked into `url`, a `params` object, or both — so both are merged here.
 */
export const parseTourRequest = (
  config: AxiosRequestConfig
): Omit<TourRequestContext, 'params'> => {
  const rawUrl = config.url ?? '';
  // `url` is relative to the instance baseURL, but tolerate an absolute one.
  const withoutOrigin = rawUrl.replace(/^https?:\/\/[^/]+/i, '');
  const [pathPart, queryPart = ''] = withoutOrigin.split('?');

  const query = new URLSearchParams(queryPart);
  const configParams = config.params as Record<string, unknown> | undefined;
  if (configParams) {
    Object.entries(configParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        let strVal: string;
        if (typeof value === 'string') {
          strVal = value;
        } else if (
          typeof value === 'number' ||
          typeof value === 'boolean' ||
          typeof value === 'bigint'
        ) {
          strVal = value.toString();
        } else {
          strVal = JSON.stringify(value);
        }
        query.set(key, strVal);
      }
    });
  }

  let body = config.data;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body);
    } catch {
      /* multipart / plain-text bodies stay as-is */
    }
  }

  let normalizedPath = pathPart;
  while (normalizedPath.length > 1 && normalizedPath.endsWith('/')) {
    normalizedPath = normalizedPath.slice(0, -1);
  }

  return {
    method: (config.method ?? 'get').toLowerCase(),
    path: normalizedPath || '/',
    query,
    body,
    config,
  };
};

/**
 * The registered route that best fits this request.
 *
 * Several patterns can match one path — `/clientOnboarding/databases` also
 * matches `/clientOnboarding/:clientId` — so the most specific wins rather than
 * whichever happened to be registered last. Ties go to the newest registration.
 */
const findRoute = (
  method: string,
  path: string
): { route: TourRoute; params: Record<string, string> } | null => {
  let best: {
    route: TourRoute;
    params: Record<string, string>;
    score: number;
  } | null = null;

  for (const route of routes) {
    if (route.method !== '*' && route.method !== method) continue;
    const match = matchPattern(route.pattern, path);
    if (!match) continue;
    // A method-specific route always beats a '*' catch-all of equal shape.
    const score = match.score + (route.method === '*' ? 0.5 : 0);
    if (!best || score < best.score) {
      best = { route, params: match.params, score };
    }
  }

  return best ? { route: best.route, params: best.params } : null;
};

/**
 * The payload an unmatched read falls back to.
 *
 * The point of the fallback is that a gap in the fixtures shows an empty
 * screen and a warning, never a crash — so it has to satisfy both shapes the
 * endpoints use. Some return a bare array the caller runs `.find()` / `.map()`
 * over; others return a `{ items, totalCount }` page. An object breaks the
 * first group and a plain array breaks the second, so this is an empty array
 * carrying the paging fields as properties: `.find()` and `.items` both work.
 *
 * It is never serialised — the adapter hands it straight to the caller — so
 * the extra properties cost nothing.
 */
const unmatchedReadPayload = () =>
  Object.assign([] as unknown[], {
    items: [],
    totalCount: 0,
    offset: 0,
    limit: 10,
    totalPages: 0,
  });

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

/**
 * Wrap a resolver's result in the standard envelope — unless it is already a
 * binary body. A caller that asked for `responseType: 'blob'` (a document
 * download, a report export) parses the response as a file, so a resolver that
 * built a Blob gets it back untouched.
 */
const buildResponse = (config: AxiosRequestConfig, payload: unknown) => {
  if (payload instanceof Blob || payload instanceof ArrayBuffer) {
    return {
      data: payload,
      status: 200,
      statusText: 'OK',
      headers: {},
      config,
    } as AxiosResponse;
  }
  return tourResponse(config, payload);
};

/**
 * Resolve a request from sample data. Never touches the network, and never
 * falls back to the live API — a tour that asked for something unfixtured gets
 * an empty result and a console warning, not a real request.
 */
export const resolveTourRequest = async (
  config: AxiosRequestConfig
): Promise<AxiosResponse> => {
  const request = parseTourRequest(config);
  const match = findRoute(request.method, request.path);

  if (match) {
    const payload = await match.route.resolve({
      ...request,
      params: match.params,
    });
    return buildResponse(config, payload);
  }

  if (MUTATING_METHODS.has(request.method)) {
    // An unfixtured write is still swallowed: tours must never reach a
    // production mutation. The simulated-mutation layer registers the writes
    // that need to change what the screen shows; this covers the rest.
    console.warn(
      `[Help & Tours] Simulated unregistered ${request.method.toUpperCase()} ${request.path} — no request was sent.`
    );
    return tourResponse(config, null, 'Saved.');
  }

  console.warn(
    `[Help & Tours] No sample data registered for GET ${request.path} — returning an empty result.`
  );
  return tourResponse(config, unmatchedReadPayload());
};

/**
 * An axios adapter that answers from sample data. Installed per-request by the
 * api-client interceptor while a tour is running, which stops axios from
 * opening a connection at all.
 */
export const tourAdapter = (config: AxiosRequestConfig) =>
  resolveTourRequest(config);

/**
 * Whether a request would be answered by a registered resolver rather than the
 * empty fallback. Used by the coverage test that asserts no screen a tour
 * visits is left unfixtured.
 */
export const hasTourRoute = (method: string, path: string): boolean =>
  findRoute(method.toLowerCase(), path) !== null;
